import mapboxgl, {
	EventData,
	MapMouseEvent,
	MapboxGeoJSONFeature,
} from "mapbox-gl";

import { MapIconColor } from "@models/enums";
import { SiteProperties } from "@models/site";

const sourceName = "sites";
const unclusteredPointsName = "sitenna-unclustered-points";

export type MarkerClickEvent = MapMouseEvent & {
	features?: MapboxGeoJSONFeature[];
} & EventData;

// adds a source to the map
export const addSiteSource = (map: mapboxgl.Map, sites: any) => {
	try {
		// if map or map.style isn't loaded yet, wait 100ms and try again
		if (!map || !map.isStyleLoaded()) {
			return setTimeout(() => addSiteSource(map, sites), 100);
		}
		const source = map.getSource(sourceName) as mapboxgl.GeoJSONSource;

		if (source) {
			source.setData(sites);
		} else {
			map.addSource(sourceName, {
				type: "geojson",
				data: sites,
				generateId: true,
			});
			addSitesLayer(map);
		}
	} catch (error) {
		console.error("addMapSource", error);
	}
};

// adds a site layer to the map
const addSitesLayer = (map: mapboxgl.Map) => {
	map.addLayer({
		id: unclusteredPointsName,
		type: "circle",
		source: sourceName,
		metadata: {
			isSiteLayer: true,
		},
		paint: {
			"circle-color": [
				"match",
				["get", "type"],
				8,
				MapIconColor.streetAsset,
				14,
				MapIconColor.greenfield,
				15,
				MapIconColor.rooftop,
				38,
				MapIconColor.tower,
				"#ccc",
			],
			"circle-radius": 5,
			"circle-stroke-width": 1.25,
			"circle-stroke-color": "#fff",
		},
	});
};

// adds site click listeners
export const addSitesClickListener = (
	map: mapboxgl.Map,
	clickCallback: (site: SiteProperties) => void,
) => {
	map.on("click", unclusteredPointsName, (e: MarkerClickEvent) => {
		const properties = e.features?.[0]?.properties as SiteProperties;

		// @ts-ignore
		const coordinates = e.features[0].geometry.coordinates.slice() as [
			number,
			number,
		];

		let [lng, lat] = coordinates;
		properties.latitude = lat;
		properties.longitude = lng;

		// Ensure that if the map is zoomed out such that multiple
		// copies of the feature are visible, the popup appears
		// over the copy being pointed to.
		while (Math.abs(e.lngLat.lng - lat) > 180) {
			lat += e.lngLat.lng > lat ? 360 : -360;
		}
		clickCallback(properties);

		if (!map.getLayer("highlightLayer")) {
			map.addLayer({
				id: "highlightLayer",
				type: "circle",
				source: sourceName,
				paint: {
					"circle-color": "transparent",
					"circle-opacity": 0.65,
					"circle-blur": 0.75,
					"circle-radius": 8.5,
					"circle-stroke-width": 3.5,
					"circle-stroke-color": "#fff",
				},
				filter: ["in", "id", properties.id],
			});
		} else {
			map.setFilter("highlightLayer", ["in", "id", properties.id]);
		}
	});

	map.on("mouseenter", unclusteredPointsName, () => {
		map.getCanvas().style.cursor = "pointer";
	});
	map.on("mouseleave", unclusteredPointsName, () => {
		map.getCanvas().style.cursor = "";
	});
};

export function getSiteTypeString(type: number): string {
	switch (type) {
		case 8:
			return "Street Asset";
		case 14:
			return "Greenfield";
		case 15:
			return "Rooftop";
		case 38:
			return "Tower";
		default:
			return "Unknown";
	}
}

export const getSiteAddress = (address1: string, address2: string): string =>
	`${address1 || ""} ${address2 || ""}`;
